/**
 * Metric Sparkline Component
 * A small, compact line chart for use in dashboard metric cards
 */

import React from "react";
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { Box, Typography, useTheme } from "@mui/material";
import { TrendingUp, TrendingDown, TrendingFlat } from "@mui/icons-material";

interface SparklineDataPoint {
  value: number;
  label?: string;
}

interface MetricSparklineProps {
  data: SparklineDataPoint[];
  height?: number;
  color?: "primary" | "success" | "error" | "warning" | "info";
  showTrend?: boolean;
  formatValue?: (value: number) => string;
}

interface SparklinePayloadData {
  value: number;
  label?: string;
}

interface TooltipPayloadItem {
  payload: SparklinePayloadData;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  formatValue: (value: number) => string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, formatValue }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Box
        sx={{
          bgcolor: "background.paper",
          px: 1,
          py: 0.5,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 0.5,
          boxShadow: 1,
        }}
      >
        <Typography variant="caption">
          {data.label ? `${data.label}: ` : ""}{formatValue(data.value)}
        </Typography>
      </Box>
    );
  }
  return null;
};

export const MetricSparkline: React.FC<MetricSparklineProps> = ({
  data,
  height = 40,
  color = "primary",
  showTrend = true,
  formatValue = (v) => v.toString(),
}) => {
  const theme = useTheme();

  const colorMap = {
    primary: theme.palette.primary.main,
    success: theme.palette.success.main,
    error: theme.palette.error.main,
    warning: theme.palette.warning.main,
    info: theme.palette.info.main,
  };

  const chartColor = colorMap[color];

  // Calculate trend
  const getTrend = () => {
    if (data.length < 2) return { direction: "flat", percentage: 0 };
    const firstValue = data[0].value;
    const lastValue = data[data.length - 1].value;

    if (firstValue === 0) return { direction: "flat", percentage: 0 };

    const change = ((lastValue - firstValue) / firstValue) * 100;

    if (Math.abs(change) < 1) return { direction: "flat", percentage: 0 };
    return {
      direction: change > 0 ? "up" : "down",
      percentage: Math.abs(change),
    };
  };

  const trend = getTrend();

  const TrendIcon = trend.direction === "up"
    ? TrendingUp
    : trend.direction === "down"
      ? TrendingDown
      : TrendingFlat;

  const trendColor = trend.direction === "up"
    ? theme.palette.success.main
    : trend.direction === "down"
      ? theme.palette.error.main
      : theme.palette.text.secondary;

  const trendText = trend.direction === "up"
    ? `Trending up ${trend.percentage.toFixed(1)}%`
    : trend.direction === "down"
      ? `Trending down ${trend.percentage.toFixed(1)}%`
      : "Stable trend";

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }} role="img" aria-label={`Sparkline chart. ${trendText}`}>
      <Box sx={{ flex: 1, minWidth: 60 }}>
        <ResponsiveContainer width="100%" height={height}>
          <AreaChart data={data} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
            <defs>
              <linearGradient id={`sparkline-${color}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={chartColor} stopOpacity={0.3} />
                <stop offset="95%" stopColor={chartColor} stopOpacity={0} />
              </linearGradient>
            </defs>
            <Tooltip content={<CustomTooltip formatValue={formatValue} />} />
            <Area
              type="monotone"
              dataKey="value"
              stroke={chartColor}
              fill={`url(#sparkline-${color})`}
              strokeWidth={1.5}
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
      {showTrend && trend.percentage > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <TrendIcon sx={{ fontSize: 16, color: trendColor }} />
          <Typography variant="caption" sx={{ color: trendColor, fontWeight: 500 }}>
            {trend.percentage.toFixed(1)}%
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MetricSparkline;
